/*!
 * Copyright 2017 - 2020 by ChartIQ, Inc.
 * All rights reserved.
 */
import { FSBLDependencyManagerSingleton as FSBLDependencyManager } from "../../common/dependencyManager";
import { System } from "../../common/system";
import { remoteReduxClientEnhancer } from "../../common/redux";
import { FinsembleWindow } from "../../common/FinsembleWindow";
import UserNotification from "../../common/toast";
import * as applicationConstants from "../../common/constants";
import { FEAGlobals } from "../../common/Globals";
import { ConfigUtilInstance as ConfigUtils } from "../../common/configUtil";
import SystemManagerClient from "../../common/systemManagerClient";
import TestPoint from "../../common/testPoint";
import Logger from "../logger";
import InteropAdmin from "../../clients/Interop/InteropAdmin";
import { RouterClient, RouterController } from "../../clients/routerClient";
import { Startup } from "../../clients/Startup/Startup";
import { AuthenticationToken } from "../Interop/types";
import { start as startSDDListeners } from "./SDDListeners";
import { getRouterConfig, getSystemStartupData, getManifestFromStartupData } from "./RouterConfig";
import AuthenticationClient from "../authenticationClient";
import { WindowClient, getControllerInstance as getWindowClientController } from "../windowClient";
import DistributedStoreClient from "../distributedStoreClient";
import LauncherClient from "../launcherClient";
import DragAndDropClient from "../../deprecated/dragAndDropClient";
import LinkerClient from "../../deprecated/linkerClient";
import StorageClient from "../storageClient";
import WorkspaceClient from "../workspaceClient";
import DialogManager from "../dialogManagerClient";
import ConfigClient from "../configClient";
import HotkeyClient from "../hotkeyClient";
import SearchClient from "../searchClient";
import NotificationClient from "../notificationClient";
import { SmartDesktopClient } from "../../deprecated/SmartDesktopClient";
import { RestartParams } from "../../services/systemManager/shutDownManager";
import { createKeys } from "../../services/Interop/AuthSignVerify";
import {
	shutdownApplication as shutdownApplicationFromDesktop,
	restartApplication as restartApplicationFromDesktop,
} from "../../common/Desktop";
import { FinsembleDesktopAgent } from "../Interop/FinsembleDesktopAgent";
import { AppDefinition, FinsembleComponentConfig } from "../../services/Interop/types";
import { FEAInfo } from "../../FEA";
import { windowStartup } from "./windowStartup";

/**
 * The launcher takes the component config and adds some pieces.
 */
type ExtendedComponentConfig = FinsembleComponentConfig & {
	// "type" is added on the fly by launcher.
	type: string;
	// "appDConfig" is added by launcher for components that come from appd.json.
	appDConfig?: AppDefinition;
	waitForInitialization: boolean;
};

const { APPLICATION_STATE_CHANNEL } = applicationConstants;

let startAppAlreadyCalled = false;

/**
 * This will be set to the system manifest, retrieved from System.getOptions().
 */
let systemManifest: any = {};

/**
 * Extracts startup data from System.getOptions() and initializes the internal variables that
 * this module depends upon such as windowName and componentEntry. This is also where
 * StartupData is initialized. Other Finsemble code, such as FinsembleDesktopAgent, depend on
 * this StartupData.
 */
const initializeStartupData = async (startup: Startup) => {
	const systemStartupData = await getSystemStartupData();
	const authenticationToken: AuthenticationToken = systemStartupData?.customData?.authenticationToken;

	const finWindow = System.Window.getCurrent();
	const windowName = finWindow.name;

	const webContentsName = finWindow.webContentsName ?? windowName;
	systemManifest = getManifestFromStartupData(systemStartupData) ?? {};

	const componentEntry: ExtendedComponentConfig = systemStartupData?.customData;

	startup.setStartupData({ windowName, webContentsName, authenticationToken });
	return {
		windowName,
		componentEntry,
		systemStartupData,
	};
};

// Attaches a click handler to the DOM when a window is loaded.
// When something on the DOM is clicked, it checks to see if its a mailto link.
// If it is a mailto link, it sets the appropriate FSBL variable (clickedMailTo).
// This allows the beforeunload event to ignore the window close
const clickedMailToWorkaround = (fsbl: FSBLDesktop) => {
	// Add an event handler to check if a mailto link was clicked on unload.
	// Essentially, if a mailto link is clicked the browser tells the OS to open the
	// default mail client, for some reason this also tells the window
	// to unload the document and its resources.
	// By setting a boolean and checking said boolean in the beforeunload/unload event
	// we can allow the action to proceed and _not_ close the window. If this event is
	// fired any other time and a mailto link _wasn't_ clicked, it will continue to close
	// NOTE: https://stackoverflow.com/questions/9740510/mailto-link-in-chrome-is-triggering-window-onbeforeunload-can-i-prevent-this
	window.addEventListener("beforeunload", (e) => {
		// Don't close the window if this was a click on a mailto link. Otherwise, reset the flag.
		if (!fsbl.clickedMailTo) {
			fsbl.windowClose();
		} else {
			fsbl.clickedMailTo = false;
			e.preventDefault();
		}
	});

	const body = document.querySelector("body");
	body?.addEventListener("click", (e) => {
		// If the target of the click is an anchor tag and if the hyperlink reference
		// includes "mailto" then set clickedMailTo to true
		if (
			(e.target as HTMLAnchorElement).tagName.toLowerCase() === "a" &&
			(e.target as HTMLAnchorElement).href.includes("mailto")
		) {
			fsbl.clickedMailTo = true;
		}
	});
};

const maybeShouldListenForSDDUpdates = (componentEntry: ExtendedComponentConfig) => {
	const isBrowserView = System.isBrowserView();
	const isTitlebarWindow = System.isTitlebarWindow();
	const isTitleBar = !isBrowserView && isTitlebarWindow;
	// Customer app content should never listen for SDD updates or have theme.css injected
	// If it's not an app or it's not a titlebar then go ahead and listen
	// In the future we might decide to support a config based opt-in for app content
	const shouldListenForSDDUpdates = !componentEntry.isAnApp || isTitleBar;

	if (shouldListenForSDDUpdates) startSDDListeners(systemManifest, componentEntry.component?.type);
};

/**
 * Run any functionality that must wait until after the DOM is loaded
 */
const onDOMContentLoaded = (fsbl: FSBLDesktop, componentEntry: ExtendedComponentConfig) => {
	maybeShouldListenForSDDUpdates(componentEntry);
	clickedMailToWorkaround(fsbl);
};

/**
 * Resolves when Finsemble has transitioned to the right state for the app to start. Finsemble
 * will send a "ready" state change to any apps that run in the authentication boot stage. Apps
 * that run in later stages may receive either a "ready" or "authenticated" state change notice.
 *
 * Will resolve to true if the state is authentication (which should only occur if the client
 * is running in an authentication app), otherwise false.
 */
const waitForApplicationState = async () =>
	new Promise<boolean>((resolve) => {
		const authorizationSubscriber = RouterClient.subscribe(APPLICATION_STATE_CHANNEL, (subscribeErr, response) => {
			// Immediately remove the state change subscription. We only want the first valid state change.
			RouterClient.unsubscribe(authorizationSubscriber);

			// Inside authentication apps, the "authenticated" state will never occur.
			const isAnAuthenticationComponent = response.data.state !== "authenticated" && response.data.state !== "ready";

			resolve(isAnAuthenticationComponent);
		});
	});

/**
 * In desktop apps, we auto-connect to the interop service if this window/view is an app.
 * This forces the linker to immediately display.
 */
const maybeConnectFDC3 = async ({
	fdc3,
	componentEntry,
}: {
	fdc3: FinsembleDesktopAgent;
	componentEntry: ExtendedComponentConfig;
}): Promise<void> => {
	// isAnApp is set to true by launcher when launching from appd.json (rather than any other config file
	const { isAnApp, interop } = componentEntry;

	// We're a content view if we're an app and we don't have a browser view. If we had a browser view then we would be the titlebar view.
	// Note that some apps don't have titlebars, or have injected titlebars. As long as they don't havea browser view then we
	// know we're in the content view. Toolbar has browser views (dashbar) but isAnApp will be false because it is a UIComponent.
	const isContentView = isAnApp && !FEAGlobals.fin?.isTitlebarWindow();

	const implicitUseLinker = isContentView;

	// The useLinker setting overrides our implicit logic for whether we have a linker
	const useLinker = interop?.useLinker;

	const implicitAutoConnect = (implicitUseLinker && useLinker !== false) || useLinker === true;

	// The autoConnect setting further overrides our implicit logic for whether to auto connect
	const autoConnect = interop?.autoConnect;

	const definitiveAutoConnect = (implicitAutoConnect && autoConnect !== false) || autoConnect === true;

	if (definitiveAutoConnect) await fdc3.start();
};

/**
 * Starts all client APIs other than Router and Logger. Currently this is WindowClient, DialogManagerClient
 * and the deprecated clients LinkerClient and DragAndDropClient.
 *
 * If the current component is an authentication component then linker and DragAndDrop are
 * not started because they are incompatible with that stage.
 */
const maybeStartClients = async () => {
	const isAuthentication = await waitForApplicationState();

	// Start the window client
	getWindowClientController().start();

	if (!isAuthentication) {
		// Initialize the deprecated LinkerClient and DragAndDropClients.
		await LinkerClient.start();
		DragAndDropClient.addListeners();
	}
};

type FSBLInfo = {
	// Version of Finsemble core API
	FSBLVersion: string;
	// Git hash of Finsemble core API
	gitHash: string;
} & FEAInfo;

export class FSBLDesktop {
	debug: boolean = false;

	FinsembleWindow: typeof FinsembleWindow = FinsembleWindow;

	ConfigUtils = ConfigUtils;

	createKeys = createKeys;

	UserNotification = UserNotification;

	SystemManagerClient: typeof SystemManagerClient = SystemManagerClient;

	// TestPoint supports Live Mocha testing -- it's not used in production.
	TestPoint: typeof TestPoint = TestPoint;

	clickedMailTo: boolean = false;

	isInitialized: boolean = false;

	displayStartupTimeoutError: boolean = true;

	dependencyManager: typeof FSBLDependencyManager = FSBLDependencyManager;

	System: typeof System = System;

	#startup = Startup.getInstance();

	remoteRedux = {
		remoteReduxClientEnhancer,
	};

	/** Namespace for internal APIs */
	internal = {
		interopAdmin: new InteropAdmin(),
	};

	componentType: string = "undefined";

	/**
	 * Apps and UI components depend on FSBL.Clients to exist during the import stage. We therefore need for these entries
	 * to exist statically in the FSBL object that is exported. The clients are still initialized dynamically when FSBL.startApp()
	 * is called.
	 */
	// This order is _roughly_ as follows: Get everything up that is a dependency of something else, then get those up. The linker and DnD depend on the Store. Workspace depends on Storage and so on. It's not exact, but this was the combination that yielded the best results for me.
	/** @namespace Clients  */
	Clients: {
		RouterClient: typeof RouterClient;
		AuthenticationClient: typeof AuthenticationClient;
		WindowClient: typeof WindowClient;
		DistributedStoreClient: typeof DistributedStoreClient;
		LauncherClient: typeof LauncherClient;
		DragAndDropClient: typeof DragAndDropClient;
		LinkerClient: typeof LinkerClient;
		StorageClient: typeof StorageClient;
		WorkspaceClient: typeof WorkspaceClient;
		DialogManager: typeof DialogManager;
		ConfigClient: typeof ConfigClient;
		HotkeyClient: typeof HotkeyClient;
		SearchClient: typeof SearchClient;
		NotificationClient: typeof NotificationClient;
		SmartDesktopClient: typeof SmartDesktopClient;
		Logger: typeof Logger;
	} = {
		RouterClient,
		AuthenticationClient,
		WindowClient,
		DistributedStoreClient,
		LauncherClient,
		DragAndDropClient,
		LinkerClient,
		StorageClient,
		WorkspaceClient,
		DialogManager,
		ConfigClient,
		HotkeyClient,
		SearchClient,
		NotificationClient,
		// This is for LTS compatibility. It provides no actual functionality.
		SmartDesktopClient,
		Logger,
	};

	#fdc3: FinsembleDesktopAgent;

	constructor({ fdc3 }: { fdc3: FinsembleDesktopAgent }) {
		this.#fdc3 = fdc3;
	}

	publishReady() {
		SystemManagerClient.publishBootStatus(this.componentType, "components", "completed");
	}

	/**
	 * Returns an FSBLInfo object that provides the current version and git hash.
	 *
	 * @param {(FSBL) => void} [cb] Optional callback to receive FSBLInfo object. Use promise if possible.
	 * @returns {FSBLInfo} Promise that resolves a FSBLInfo object
	 */
	async getFSBLInfo(cb?: (fsblInfo: FSBLInfo) => void): Promise<FSBLInfo> {
		await this.#startup.onReady();
		const feaInfo = await this.System.getFEAInfo();
		const { err, data: config } = await this.Clients.ConfigClient.getValue({ field: "finsemble" });
		if (err || !config) {
			return {
				...feaInfo,
				FSBLVersion: "unknown",
				gitHash: "unknown",
			};
		}
		const info = {
			...feaInfo,
			FSBLVersion: config.system.FSBLVersion,
			gitHash: config.system.gitHash,
		};

		if (cb) {
			cb(info);
		}
		return info;
	}

	/**
	 * Register to receive a callback when FSBL is ready. This is part of the pattern
	 * that is recommended to app builders using the FSBL API.
	 * ```
	 * if (window.FSBL && FSBL.addEventListener) {
	 * 	FSBL.addEventListener("onReady", init);
	 * } else {
	 * 	window.addEventListener("FSBLReady", init);
	 * }
	 * ```
	 */
	addEventListener(listenerType: "onReady", callback: () => void) {
		this.#startup.onReady(callback);
	}

	/**
	 * Shutdown the SmartDesktop (Finsemble).
	 */
	shutdownApplication() {
		shutdownApplicationFromDesktop();
	}

	/**
	 * Restart the SmartDesktop (Finsemble).
	 * @param [params]
	 * @param [params.forceRestart]=false Whether to give components a chance to shutdown. ForceRestart is used during the reset sequence.
	 */
	restartApplication(params?: RestartParams) {
		restartApplicationFromDesktop(params);
	}

	/**
	 * Deprecated. This is supposed to provide an optional callback during system shutdown
	 * but it hasn't been functional in many versions.
	 * @deprecated
	 */
	onShutdown() {
		Logger.system.deprecated(`FSBL.onShutdown(). This has been non-functional for many versions.`);
	}

	/**
	 * Invokes the onClose method of each API client when the window is closed.
	 *
	 * The three clients that currently require an onClose event are:
	 * - hotkeyClient (to deregister global hotkeys that were added by this app) - better handled by the hotkeys service by listening for closed windows
	 * - linkerClient (to remove distributed store listeners) - linkerClient is deprecated
	 * - notificationClient (to remove notification subscriptions) - better handled by the notification service by listening for closed windows
	 *
	 * FinsembleWindow.closeAllWraps() - Removes listeners in the window service. - better handled by the window service itself.
	 *
	 * RouterClient.disconnectAll() - Removes all registered responders, listeners and subscribers. - This can probably be handled by the router service, e.g. by detecting transport disconnect or by handling window close events.
	 */
	windowClose() {
		for (const clientKey in this.Clients) {
			const client = this.Clients[clientKey];
			if (client.onClose) {
				client.onClose();
			}
		}
		FinsembleWindow.closeAllWraps();
	}

	/**
	 * Depending on config, signals the window service and boot system that the component/window has been successfully
	 * started. This is never done inside a BrowserView (because the parent BrowserWindow will have already
	 * send these signals).
	 *
	 * @param {string} name - The name of this instance (windowName)
	 * @param {string} type - The component type of this instance
	 */
	maybeSignalSuccessfulStart(name: string, type: string, waitForInitialization: boolean) {
		if (!System.isBrowserView()) {
			// Always publish this part, which is independent of startup.
			RouterClient.publish(`Finsemble.${name}.componentReady`, {
				name,
			});

			// If appD config indicates the app will not do the publish, then do it here
			if (!waitForInitialization) {
				SystemManagerClient.publishBootStatus(type, "components", "completed");
				Logger.system.log("publishBootStatus", type, "components", "completed", systemManifest.customData);
			} else {
				Logger.system.log(`waitForInitialization for ${name}`);
			}
		}
	}

	/**
	 * Starts the client API (running in this app). This is called automatically by the preload (FSBL.js aka finsemble-desktop-api-preload.ts)
	 * which is injected by the launcher service.
	 *
	 * Any usage of client APIs prior to this function completing is indeterminant.
	 *
	 * Within this function we perform a number of steps including:
	 * - Extraction of startup data (from options.customData)
	 * - Starting of the router
	 * - Creation of the global finsembleWindow
	 * - initialization of the FSBL client APIs
	 * - Registering of error handlers and other miscellany
	 */
	startApp() {
		return new Promise<string | null>(async (resolve) => {
			const { windowName, componentEntry, systemStartupData } = await initializeStartupData(this.#startup);

			Logger.console.debug(`WINDOW LIFECYCLE:STARTUP: startApp invoked in ${windowName}`);

			if (startAppAlreadyCalled) {
				console.warn(`FSBL.startApp called twice.`);
				return resolve(null);
			}
			startAppAlreadyCalled = true;

			// imesner: 20220405: Capture securitypolicyviolation events and log as errors.
			// This must be added before the dom is loaded in order to capture them
			// These are checked in e2e here:
			// packages\e2e-testing\features\tests\Finsemble\Functional\0_System\Content Security Policies.feature
			document.addEventListener("securitypolicyviolation", (e) => {
				Logger.system.error("securitypolicyviolation:", e);
			});

			const routerConfig = getRouterConfig({
				windowName,
				systemManifest,
			});

			await RouterController.start(routerConfig);

			Logger.console.debug(`WINDOW LIFECYCLE:STARTUP: Router is ready in ${windowName}`);

			RouterClient.transmit(`${windowName}.onSpawned`, { name: windowName }, { suppressWarnings: true });

			try {
				const { wrap: win } = await FinsembleWindow.getInstance({
					name: windowName,
				});
				// Race Condition Alert: For windows opened with window.open rather than spawn (like the Toolbar menus), the fin.options
				// are only accurate right here at this moment, after having awaited for getInstance(). There's kind of
				// a chicken and egg problem as a result because we can't initialize the router client without those options
				// and we don't have the final options until they arrive from the router. Practically, this isn't a problem
				// because the options which are available at the time we initialize the router client are those of the
				// parent (opener). Ultimately, the way we use window.open needs
				// refactoring (e.g. options should be set in FEA *before* this preload is running).
				FEAGlobals.finsembleWindow = win;
			} catch (err) {
				// We cannot get an instance of a window in a service.
				if (err !== "Cannot Wrap Service Manager or Services") {
					Logger.console.error(`Unable to get FinsembleWindow instance for ${windowName}`, err);
				}
			}
			await maybeStartClients();

			await windowStartup({
				windowName,
				componentType: componentEntry.component?.type,
				finsembleWindow: FEAGlobals.finsembleWindow,
				systemStartupData,
			});

			// Add the CSS class "finsemble" to the app's HTML. Apps can use this for responsive styling.
			document.getElementsByTagName("html")[0].classList.add("finsemble");

			// save type in cased needed for publishReady(), needs to happen before setReady()
			this.componentType = componentEntry.component?.type;

			this.#startup.setReady();
			Logger.start();

			await maybeConnectFDC3({ fdc3: this.#fdc3, componentEntry });

			Logger.system.debug(`WINDOW LIFECYCLE:STARTUP: FSBL Online in ${windowName}`, componentEntry);

			this.maybeSignalSuccessfulStart(windowName, componentEntry.component?.type, componentEntry.waitForInitialization);

			// Run any logic that must wait until the DOM is loaded
			if (document.readyState === "loading") {
				document.addEventListener("DOMContentLoaded", () => {
					onDOMContentLoaded(this, componentEntry);
				});
			} else {
				onDOMContentLoaded(this, componentEntry);
			}

			resolve(null);
		});
	}

	/**
	 * Stops the app. This primarily closes the router and terminates each of the FSBL clients.
	 */
	stopApp() {
		startAppAlreadyCalled = false;
		this.windowClose();
		RouterController.stop();
	}
}
